import React from "react";
import { Building2, Truck, Flag, CheckCircle2, ShieldCheck, Clock } from "lucide-react";
import { useT } from "../lib/lang";

const STATUS_STYLE = {
  dispatched: { bg: "bg-neutral-200", text: "text-rescue-ink", Icon: Clock },
  acknowledged: { bg: "bg-sky-600", text: "text-white", Icon: ShieldCheck },
  en_route: { bg: "bg-amber-600", text: "text-white", Icon: Truck },
  on_scene: { bg: "bg-emerald-600", text: "text-white", Icon: Flag },
  closed: { bg: "bg-rescue-ink", text: "text-white", Icon: CheckCircle2 },
  cancelled: { bg: "bg-rescue-red", text: "text-white", Icon: CheckCircle2 },
};

const fmt = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch (_e) { return iso; }
};

/**
 * Compact intervention status pill for inline use (history rows).
 */
export const InterventionPill = ({ alert }) => {
  const [t] = useT();
  const status = alert?.intervention?.status || null;
  if (!status) return null;
  const style = STATUS_STYLE[status] || STATUS_STYLE.dispatched;
  const { Icon } = style;
  const label = t.interventionStatus?.[status] || status;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] ${style.bg} ${style.text}`}
      data-testid={`intervention-pill-${alert.id}`}
    >
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
};

/**
 * Larger intervention card shown on the share/details page.
 */
export const InterventionCard = ({ alert }) => {
  const [t] = useT();
  const i = alert?.intervention;
  return (
    <div className="mx-5 mb-5 border-2 border-rescue-line bg-white" data-testid="intervention-card">
      <div className="px-4 py-3 border-b-2 border-rescue-line text-[10px] font-bold uppercase tracking-[0.25em] text-rescue-mute flex items-center gap-1.5">
        <Building2 className="w-3.5 h-3.5" /> {t.intervention}
      </div>
      {!i ? (
        <div className="px-4 py-4 text-xs text-rescue-mute">{t.noAgencyYet}</div>
      ) : (
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs">
              <div className="text-[10px] uppercase tracking-[0.2em] text-rescue-mute">{t.agencyHandling}</div>
              <div className="font-bold mt-0.5 truncate">{i.agency_name || "—"}</div>
            </div>
            {(() => {
              const style = STATUS_STYLE[i.status] || STATUS_STYLE.dispatched;
              const { Icon } = style;
              return (
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${style.bg} ${style.text}`}>
                  <Icon className="w-3 h-3" /> {t.interventionStatus?.[i.status] || i.status}
                </span>
              );
            })()}
          </div>
          <ul className="text-[11px] text-rescue-mute space-y-0.5 font-mono">
            {i.acknowledged_at && <li>· acceptée — {fmt(i.acknowledged_at)}</li>}
            {i.en_route_at && <li>· en route — {fmt(i.en_route_at)}</li>}
            {i.on_scene_at && <li>· sur place — {fmt(i.on_scene_at)}</li>}
            {i.closed_at && <li>· clôturée — {fmt(i.closed_at)}</li>}
          </ul>
          {i.note && <p className="text-[12px] text-rescue-ink italic mt-1">« {i.note} »</p>}
        </div>
      )}
    </div>
  );
};
